import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { Send as SendIcon, RestartAlt as RestartAltIcon, SmartToy as BotIcon } from '@mui/icons-material';
import useApiCall from '../../hooks/useApiCall';
import useToast from '../../hooks/useToast';
import { usePageMeta } from '../../contexts/PageMetaContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import SanitizedHtml from '../../components/SanitizedHtml';
import { INDIGO, SLATE } from '../../constants/Colors';

const SANDBOX_POLL_INTERVAL_MS = 3000;

const PANEL_HEIGHT = 'calc(100vh - 220px)';

function UserBubble({ text }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Box
        sx={{
          maxWidth: 560,
          bgcolor: INDIGO[600],
          color: '#fff',
          borderRadius: '12px',
          px: 2.5,
          py: 1.5,
        }}
      >
        <SanitizedHtml content={text} sx={{ color: '#fff' }} />
      </Box>
    </Box>
  );
}

function AssistantBubble({ text }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${INDIGO[500]} 0%, #7c3aed 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <BotIcon sx={{ fontSize: '1rem', color: '#fff' }} />
      </Box>
      <Box
        sx={{
          maxWidth: 560,
          bgcolor: '#fff',
          border: `1px solid ${SLATE[200]}`,
          borderRadius: '12px',
          px: 2.5,
          py: 1.5,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        <SanitizedHtml content={text} sx={{ color: SLATE[800] }} />
      </Box>
    </Box>
  );
}

export default function AgentBotTesting() {
  const { id: agentId } = useParams();
  const tokens = useSelector(state => state?.tokens.value);
  const currentApp = useSelector(state => state?.apps?.selectedApp);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();
  const { error: toastError } = useToast();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [agentBuilding, setAgentBuilding] = useState(false);

  const listRef = useRef(null);
  const healthPollRef = useRef(null);
  const healthPollCountRef = useRef(0);
  const tokensRef = useRef(tokens);

  useEffect(() => { tokensRef.current = tokens; }, [tokens]);

  const HEALTH_POLL_MAX = 10;

  const endpoint = `/v1/apps/${currentApp?.id}/agents/${agentId}/test_conversation`;
  const sandboxHealthEndpoint = `/v1/apps/${currentApp?.id}/agents/${agentId}/test_conversation/sandbox_health`;

  const stopHealthPoll = () => {
    if (healthPollRef.current) {
      clearInterval(healthPollRef.current);
      healthPollRef.current = null;
    }
  };

  const startHealthPoll = () => {
    stopHealthPoll();
    healthPollCountRef.current = 0;
    healthPollRef.current = setInterval(async () => {
      try {
        const data = await makeRequest(sandboxHealthEndpoint, {
          method: 'GET',
          authTokens: tokensRef.current,
          ignore_error: true,
        });
        if (data?.status === 'ok') {
          setAgentBuilding(false);
          stopHealthPoll();
          return;
        }
      } catch (_err) {
        // silently ignore per-attempt errors
      }
      healthPollCountRef.current += 1;
      if (healthPollCountRef.current >= HEALTH_POLL_MAX) {
        setAgentBuilding(false);
        stopHealthPoll();
        toastError('Something went wrong while building the agent. Please try again.');
      }
    }, SANDBOX_POLL_INTERVAL_MS);
  };

  useEffect(() => {
    setPageMeta({ description: 'Send test messages to your agent and see how it responds.' });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentApp?.id || !agentId) return;

    let cancelled = false;
    setLoading(true);

    makeRequest(endpoint, { method: 'GET', authTokens: tokens })
      .then(data => {
        if (!cancelled) {
          setMessages(data?.test_conversation?.messages || []);
          if (data?.agent_restarted === 'true' || data?.agent_restarted === true) {
            setAgentBuilding(true);
            startHealthPoll();
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      stopHealthPoll();
    };
  }, [currentApp?.id, agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || sending) return;

    const optimisticMessage = { role: 'user', text: trimmed, at: null };
    setMessages(prev => [...prev, optimisticMessage]);
    setInputValue('');
    setSending(true);

    try {
      const data = await makeRequest(endpoint, {
        method: 'POST',
        body: { message: trimmed },
        authTokens: tokens,
      });
      if (data?.test_conversation?.messages) {
        setMessages(data.test_conversation.messages);
      }
    } catch (_err) {
      setMessages(prev => prev.filter(msg => msg !== optimisticMessage));
      setInputValue(trimmed);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = async () => {
    if (clearing || sending) return;
    setClearing(true);
    try {
      await makeRequest(endpoint, { method: 'DELETE', authTokens: tokens });
      setMessages([]);
    } catch (_err) {
      // generic error toast already shown by useApiCall
    } finally {
      setClearing(false);
    }
  };

  const busy = sending || clearing || agentBuilding;

  return (
    <Box
      sx={{
        width: '100%',
        height: PANEL_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        border: `1px solid ${SLATE[200]}`,
        borderRadius: '16px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${SLATE[200]}`,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: SLATE[900] }}>
          Test Conversation
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RestartAltIcon sx={{ fontSize: '1rem' }} />}
          onClick={handleClear}
          disabled={loading || busy}
          sx={{ textTransform: 'none', fontSize: '0.8125rem', fontWeight: 500 }}
        >
          Fresh start
        </Button>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <Box
          ref={listRef}
          sx={{
            height: '100%',
            overflowY: 'auto',
            px: 4,
            py: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          {loading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LoadingSpinner message="Loading conversation..." subMessage="" />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: '0.875rem', color: SLATE[500] }}>
                No messages yet — send a message to test your agent.
              </Typography>
            </Box>
          ) : (
            messages.map((msg, index) =>
              msg.role === 'user' ? (
                <UserBubble key={index} text={msg.text} />
              ) : (
                <AssistantBubble key={index} text={msg.text} />
              )
            )
          )}
        </Box>

        {agentBuilding && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              bgcolor: 'rgba(255,255,255,0.88)',
              backdropFilter: 'blur(3px)',
              zIndex: 10,
            }}
          >
            <CircularProgress size={36} thickness={4} sx={{ color: INDIGO[500] }} />
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: SLATE[700] }}>
              Agent is building…
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: SLATE[500] }}>
              Please wait while your agent restarts.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Input */}
      <Box
        sx={{
          borderTop: `1px solid ${SLATE[200]}`,
          px: 3,
          py: 2,
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-end',
          flexShrink: 0,
        }}
      >
        <TextField
          multiline
          maxRows={5}
          fullWidth
          placeholder="Type a message to test your agent..."
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading || sending || agentBuilding}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: '0.875rem',
              bgcolor: '#fff',
              '& fieldset': { borderColor: SLATE[300] },
              '&:hover fieldset': { borderColor: SLATE[400] },
              '&.Mui-focused fieldset': { borderColor: INDIGO[500] },
            },
            '& .MuiInputBase-input': { color: SLATE[800], py: '12px' },
          }}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={loading || sending || agentBuilding || !inputValue.trim()}
          endIcon={<SendIcon sx={{ fontSize: '1rem !important' }} />}
          sx={{
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            px: 2.5,
            py: '12px',
            flexShrink: 0,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
}
